import { useCallback, useEffect, useRef, useState } from "react";
import {
  RoomConnection,
  createRoom,
  fetchRoomInfo,
  loadRoomSession,
  saveRoomSession,
  clearRoomSession,
} from "./roomClient";

const ERROR_TEXTS = {
  name_required: "Bitte gib einen Namen ein.",
  room_full: "Der Raum ist voll.",
  player_not_found: "Diesen Platz gibt es nicht mehr.",
  player_online: "Dieser Platz ist gerade belegt.",
};

/**
 * Kapselt die Raum-Verbindung: beitreten, Namen setzen, Lobby beobachten.
 * Die Verbindung überlebt Re-Renders (Ref) und baut sich selbst wieder auf.
 */
export function useRoom() {
  const [session, setSession] = useState(() => loadRoomSession());
  const [room, setRoom] = useState(null);
  const [role, setRole] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [kicked, setKicked] = useState(false);
  // Lösung liegt erst nach bewusstem Aufdecken auf dem Gerät
  const [revealedSolution, setRevealedSolution] = useState(null);
  // Eigener Jahres-Tipp der laufenden Runde (bestätigt vom Raum)
  const [myYearGuess, setMyYearGuess] = useState(null);
  // Eigener Spielerplatz — beim Host erst nach hostJoin gesetzt
  const [myPlayerId, setMyPlayerId] = useState(null);
  // Ein neueres Host-Fenster hat diesen Raum uebernommen
  const [abgeloest, setAbgeloest] = useState(false);

  const connRef = useRef(null);
  const lastRoundRef = useRef(undefined);
  // Zuletzt gesendeter Host-Schnappschuss (siehe sendHostSync)
  const lastSyncRef = useRef("");
  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const disconnect = useCallback(() => {
    if (connRef.current) {
      connRef.current.close();
      connRef.current = null;
    }
    setStatus("idle");
    setRoom(null);
    setRole(null);
  }, []);

  const openConnection = useCallback((code, token) => {
    if (connRef.current) connRef.current.close();
    // Diese Verbindung ist ab jetzt die juengste — eine fruehere Abloesung
    // gilt damit nicht mehr.
    setAbgeloest(false);

    const conn = new RoomConnection({
      code,
      token,
      onStatus: (s) => {
        // Neue Verbindung: den Stand auf jeden Fall nochmal schicken. Ging
        // der letzte Sync ueber einen toten Socket raus, galt er als
        // gesendet — und wurde nach dem Wiederaufbau nie nachgeholt.
        if (s === "connected") lastSyncRef.current = "";
        setStatus(s);
      },
      onGone: () => {
        // Raum ist abgelaufen. Sitzung wegwerfen, damit man nicht bei jedem
        // Reload erneut in einen toten Raum läuft.
        clearRoomSession(sessionRef.current?.role);
        setSession(null);
        setRoom(null);
        setRole(null);
        setError("Der Raum ist abgelaufen.");
      },
      onState: (next) => {
        // Beim Songwechsel muss die aufgedeckte Lösung vom Gerät verschwinden,
        // sonst steht die Antwort der Vorrunde noch da.
        const nextRound = next?.game?.roundCount;
        if (nextRound !== undefined && nextRound !== lastRoundRef.current) {
          lastRoundRef.current = nextRound;
          setRevealedSolution(null);
          setMyYearGuess(null);
        }
        setRoom(next);
      },
      onMessage: (msg) => {
        if (msg.type === "welcome") {
          setRole(msg.role);
          if (msg.playerId) setMyPlayerId(msg.playerId);
        }
        if (msg.type === "hostJoined") {
          setMyPlayerId(msg.playerId);
        }
        if (msg.type === "hostSuperseded") {
          // Ein anderes Fenster hostet diesen Raum jetzt. Hier nichts mehr
          // senden — sonst schreiben beide abwechselnd ihren Spielstand.
          setAbgeloest(true);
          // Und auch nicht von selbst wiederverbinden: der automatische
          // Wiederaufbau nach dem Standby meldete sich mit dem Host-Token an,
          // holte sich damit die Steuerung zurueck — blieb aber stumm. Dann
          // schwiegen beide Fenster und das Spiel stand. Zurueck geht es nur
          // bewusst, per Neuladen (darauf weist das Banner hin).
          conn.close();
          if (connRef.current === conn) connRef.current = null;
          setStatus("idle");
        }
        if (msg.type === "solution") {
          setRevealedSolution(msg.solution);
        }
        if (msg.type === "yearAccepted") {
          setMyYearGuess(msg.year);
        }
        if (msg.type === "joined") {
          // Token merken — nur damit findet das Handy nach dem Standby
          // wieder auf denselben Spielerplatz zurück.
          conn.setToken(msg.token);
          const next = {
            code,
            role: "player",
            token: msg.token,
            playerId: msg.playerId,
          };
          setSession(next);
          saveRoomSession(next);
          setRole("player");
          setMyPlayerId(msg.playerId);
          setError(null);
        }
        if (msg.type === "error") {
          setError(ERROR_TEXTS[msg.code] || "Etwas hat nicht geklappt.");
        }
        if (msg.type === "kicked") {
          // Verbindung SOFORT dichtmachen: sonst greift der automatische
          // Wiederaufbau und der Rausgeworfene landet als Gast wieder in
          // derselben Lobby, ohne zu merken, dass er draussen ist.
          conn.close();
          if (connRef.current === conn) connRef.current = null;
          setStatus("idle");
          setRoom(null);
          setRole(null);
          setKicked(true);
          clearRoomSession("player");
          setSession(null);
        }
      },
    });

    connRef.current = conn;
    conn.connect();
    return conn;
  }, []);

  // Nach einem Reload zurück in den Raum — aber nur, wenn er noch existiert.
  // Vorher wurde blind verbunden: nach einem alten Spieleabend hing man
  // dadurch minutenlang in einer Lobby, die es nicht mehr gab, und kam
  // ohne "Raum verlassen" nicht heraus.
  useEffect(() => {
    let abgebrochen = false;

    const wiederherstellen = async () => {
      const saved = sessionRef.current;
      if (!saved || !saved.code || connRef.current) return;

      const info = await fetchRoomInfo(saved.code).catch(() => "unbekannt");
      if (abgebrochen) return;

      // Während der Abfrage kann längst ein anderer Raum betreten worden sein
      // (gescannter oder eingetippter Code). Dann darf dieser verspätete
      // Wiederaufbau die frische Verbindung NICHT überschreiben.
      if (connRef.current) return;
      if (sessionRef.current !== saved) return;

      if (info === null) {
        // Raum ist definitiv weg — Sitzung wegwerfen statt anzufunken
        clearRoomSession(saved.role);
        setSession(null);
        return;
      }
      // Bei Netzproblemen ("unbekannt") trotzdem versuchen
      openConnection(saved.code, saved.token);
    };

    wiederherstellen();

    return () => {
      abgebrochen = true;
      if (connRef.current) connRef.current.close();
      connRef.current = null;
    };
    // Nur einmal beim Start
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hostOpenLobby = useCallback(
    async (settings) => {
      setError(null);
      const created = await createRoom(settings);
      const next = { code: created.code, role: "host", token: created.hostToken };
      setSession(next);
      saveRoomSession(next);
      openConnection(created.code, created.hostToken);
      return created.code;
    },
    [openConnection]
  );

  /** Prüft den Code und öffnet die Verbindung (noch ohne Namen). */
  const enterRoom = useCallback(
    async (rawCode) => {
      setError(null);
      setKicked(false);
      const code = String(rawCode || "").trim().toUpperCase();
      if (code.length !== 4) {
        setError("Der Code besteht aus 4 Zeichen.");
        return null;
      }
      const info = await fetchRoomInfo(code).catch(() => null);
      if (!info) {
        setError("Diesen Raum gibt es nicht (mehr).");
        return null;
      }

      // Ein neuer Code verdrängt die alte Sitzung — auch wenn er von Hand
      // eingetippt wurde und nicht über den QR-Code kam.
      const saved = loadRoomSession("player");
      if (saved && saved.code !== code) {
        clearRoomSession("player");
        setSession(null);
        openConnection(code, "");
        return info;
      }

      // Derselbe Raum, und wir haben noch ein Token: damit zurück auf den
      // EIGENEN Platz. Ohne das verband man sich als Gast, der alte Platz
      // galt als belegt (die eigene Verbindung hielt ihn) und man kam nicht
      // mehr als dieselbe Person herein.
      openConnection(code, saved?.token || "");
      return info;
    },
    [openConnection]
  );

  const submitName = useCallback((name) => {
    setError(null);
    if (!connRef.current) return false;
    return connRef.current.send({ type: "join", name });
  }, []);

  const claimSlot = useCallback((playerId) => {
    setError(null);
    if (!connRef.current) return false;
    return connRef.current.send({ type: "claim", playerId });
  }, []);

  const kickPlayer = useCallback((playerId) => {
    if (!connRef.current) return false;
    return connRef.current.send({ type: "kick", playerId });
  }, []);

  // Host schickt den Spielstand. Unveränderte Schnappschüsse werden
  // verworfen — sonst löst jedes Re-Render eine Nachricht aus.
  const abgeloestRef = useRef(false);
  useEffect(() => {
    abgeloestRef.current = abgeloest;
  }, [abgeloest]);
  const sendHostSync = useCallback((state) => {
    if (!connRef.current) return false;
    // Nur der Host spricht fuer das Spiel (der Raum lehnt es sonst ohnehin ab)
    if (sessionRef.current?.role && sessionRef.current.role !== "host") return false;
    // Abgeloestes Fenster schweigt: sein Spielstand ist veraltet.
    if (abgeloestRef.current) return false;
    const payload = JSON.stringify(state);
    if (payload === lastSyncRef.current) return false;
    const sent = connRef.current.send({ type: "hostSync", state });
    // Nur merken, wenn es auch rausging — sonst bliebe nach einem
    // Verbindungsabriss der letzte Stand ungesendet.
    if (sent) lastSyncRef.current = payload;
    return sent;
  }, []);

  // --- Buzzer ---
  const buzz = useCallback(() => {
    if (!connRef.current) return false;
    return connRef.current.send({ type: "buzz" });
  }, []);

  const requestSolution = useCallback(() => {
    if (!connRef.current) return false;
    return connRef.current.send({ type: "reveal" });
  }, []);

  const reportAnswer = useCallback((correct) => {
    if (!connRef.current) return false;
    return connRef.current.send({ type: "selfReport", correct: !!correct });
  }, []);

  // Host loest die Runde fuer alle auf ("keiner weiss es"). Die Loesung
  // liegt bereits im Raum-Objekt und wird von dort verteilt.
  const revealToAll = useCallback(() => {
    if (!connRef.current) return false;
    return connRef.current.send({ type: "hostReveal" });
  }, []);

  /** Jahres-Runde: eigenen Tipp abgeben. */
  const sendYearGuess = useCallback((year) => {
    if (!connRef.current) return false;
    return connRef.current.send({ type: "yearGuess", year });
  }, []);

  /** Jahres-Runde auswerten lassen (nur Host). */
  const revealYear = useCallback(() => {
    if (!connRef.current) return false;
    return connRef.current.send({ type: "yearReveal" });
  }, []);

  const hostJoinAsPlayer = useCallback((name) => {
    if (!connRef.current) return false;
    return connRef.current.send({ type: "hostJoin", name });
  }, []);

  const leaveRoom = useCallback(() => {
    disconnect();
    clearRoomSession(sessionRef.current?.role);
    setSession(null);
    setKicked(false);
  }, [disconnect]);

  return {
    session,
    room,
    role,
    status,
    error,
    kicked,
    abgeloest,
    hostOpenLobby,
    enterRoom,
    submitName,
    claimSlot,
    kickPlayer,
    sendHostSync,
    buzz,
    requestSolution,
    reportAnswer,
    revealToAll,
    sendYearGuess,
    revealYear,
    myYearGuess,
    hostJoinAsPlayer,
    revealedSolution,
    myPlayerId,
    leaveRoom,
    setError,
  };
}
